import type { Edge, Node } from '@xyflow/react';
import type { FindingAttackPathDto } from '@terra/shared-contracts-findings';
import { AI_COMPONENT_KINDS } from './finding-attack-path-ai-marks';

export type FindingAttackPathFlowNodeData = {
  entityKind: string;
  label: string;
  sublabel: string | null;
  kind: FindingAttackPathDto['nodes'][number]['kind'];
  highlighted: boolean;
  severity: string | null;
  signalId: string | null;
  findingId: string | null;
  isCurrent: boolean;
  assetId: string | null;
  assetKind: string | null;
  entryType: 'INTERNET' | 'CONNECTOR' | null;
  credentialId: string | null;
  roleName: string | null;
};

export type FindingAttackPathFlowEdgeData = {
  relation: FindingAttackPathDto['edges'][number]['relation'];
  variant: FindingAttackPathDto['edges'][number]['variant'];
};

const DISCOVERED_ASSET_KINDS = new Set([
  'NETBLOCK',
  'DOMAIN',
  'HOST',
  'GATEWAY',
  'NETWORK_SERVICE',
  'SOFTWARE',
  'STORAGE_SERVICE',
]);

export function attackPathAssetNavigation(assetKind: string): {
  type: 'ENDPOINT' | 'WEBPAGE' | 'DISCOVERED_ASSET' | 'AI_COMPONENT';
  discoveredAssetType?: string;
} | null {
  if (assetKind === 'ENDPOINT' || assetKind === 'WEBPAGE') {
    return { type: assetKind };
  }

  if (AI_COMPONENT_KINDS.has(assetKind)) {
    return { type: 'AI_COMPONENT' };
  }

  if (DISCOVERED_ASSET_KINDS.has(assetKind)) {
    return {
      type: 'DISCOVERED_ASSET',
      discoveredAssetType: assetKind,
    };
  }

  return null;
}

export function buildFindingAttackPathFlow(attackPath: FindingAttackPathDto): {
  nodes: Node<FindingAttackPathFlowNodeData>[];
  edges: Edge<FindingAttackPathFlowEdgeData>[];
} {
  return {
    nodes: attackPath.nodes.map((node) => ({
      id: node.id,
      type: 'findingAttackPathNode',
      position: { x: 0, y: 0 },
      data: toNodeData(node),
    })),
    edges: attackPath.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: 'findingAttackPathEdge',
      data: {
        relation: edge.relation,
        variant: edge.variant,
      },
    })),
  };
}

function toNodeData(
  node: FindingAttackPathDto['nodes'][number],
): FindingAttackPathFlowNodeData {
  if (node.kind === 'ENTRY') {
    return {
      entityKind: node.entryType,
      label: node.label || node.networkName || '',
      sublabel: node.networkName,
      kind: node.kind,
      highlighted: false,
      severity: null,
      signalId: null,
      findingId: null,
      isCurrent: false,
      assetId: null,
      assetKind: null,
      entryType: node.entryType,
      credentialId: null,
      roleName: null,
    };
  }

  if (node.kind === 'ASSET') {
    return {
      entityKind: node.assetKind,
      label: node.label,
      sublabel: node.sublabel,
      kind: node.kind,
      highlighted: false,
      severity: null,
      signalId: null,
      findingId: null,
      isCurrent: false,
      assetId: node.assetId,
      assetKind: node.assetKind,
      entryType: null,
      credentialId: null,
      roleName: null,
    };
  }

  if (node.kind === 'WEAKNESS') {
    return {
      entityKind: 'WEAKNESS',
      label: node.title,
      sublabel: null,
      kind: node.kind,
      highlighted: false,
      severity: null,
      signalId: node.signalId,
      findingId: null,
      isCurrent: false,
      assetId: null,
      assetKind: null,
      entryType: null,
      credentialId: null,
      roleName: null,
    };
  }

  if (node.kind === 'CREDENTIAL') {
    return {
      entityKind: 'CREDENTIAL',
      label: node.label || node.username || '',
      sublabel: node.roleName,
      kind: node.kind,
      highlighted: false,
      severity: null,
      signalId: null,
      findingId: null,
      isCurrent: false,
      assetId: null,
      assetKind: null,
      entryType: null,
      credentialId: node.credentialId,
      roleName: node.roleName,
    };
  }

  return {
    entityKind: 'FINDING',
    label: node.title,
    sublabel: null,
    kind: node.kind,
    highlighted: node.isCurrent,
    severity: node.severity,
    signalId: null,
    findingId: node.findingId,
    isCurrent: node.isCurrent,
    assetId: null,
    assetKind: null,
    entryType: null,
    credentialId: null,
    roleName: null,
  };
}
